import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

/**
 * Shapes a notification from either source - the list API or a live FCM
 * push - into one structure, so the drawer never has to branch on origin.
 *
 * List API item:
 *   { id, to: {id, email, name}, title, body, data: {type, xbucket_id,
 *     feedback_id}, is_read, read_at, sent_at, status, created_at }
 */
const normalizeNotification = (n = {}) => ({
  id: n.id,
  title: n.title || "",
  body: n.body || "",
  // Deep-link ids live here: { type: "PTP", xbucket_id, feedback_id }.
  // FCM delivers every value as a string, so the API matches that already.
  data: n.data || {},
  is_read: !!n.is_read,
  read_at: n.read_at || null,
  sent_at: n.sent_at || null,
  status: n.status || null,
  created_at: n.created_at || null,
  to: n.to || null,
});

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (_, thunkAPI) => {
    try {
      const { token } = thunkAPI.getState().auth;
      const response = await axios.get("auth/notifications/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // The paginated page sits inside a `data` envelope:
      //   { status, message, data: { count, next, previous, results: [...] } }
      // Falling back to the un-enveloped body keeps this working if the API
      // is ever flattened.
      const page = response.data?.data ?? response.data ?? {};

      return {
        results: (page.results || []).map(normalizeNotification),
        count: page.count ?? 0,
        next: page.next ?? null,
        previous: page.previous ?? null,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch notifications"
      );
    }
  }
);

/**
 * POST auth/notifications/mark-read/ with { notification_id }.
 * The endpoint takes a single id, so this is dispatched per notification
 * rather than as a "mark all read" sweep.
 */
export const markNotificationRead = createAsyncThunk(
  "notifications/markNotificationRead",
  async (notificationId, thunkAPI) => {
    try {
      const { token } = thunkAPI.getState().auth;
      await axios.post(
        "auth/notifications/mark-read/",
        { notification_id: notificationId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return notificationId;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to mark notification as read"
      );
    }
  }
);

const initialState = {
  notifications: [],
  count: 0,
  next: null,
  previous: null,
  loading: false,
  error: null,
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    addRealtimeNotification: (state, action) => {
      const incoming = normalizeNotification(action.payload);
      // A push can land while its persisted copy is already in the list
      // (or arrive twice across tabs) - keep one entry per id.
      if (incoming.id != null) {
        state.notifications = state.notifications.filter(
          (n) => n.id !== incoming.id
        );
      }
      state.notifications.unshift(incoming);
      state.count += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.results;
        state.count = action.payload.count;
        state.next = action.payload.next;
        state.previous = action.payload.previous;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })
      // Applied on success rather than optimistically: a failed call must not
      // leave the badge showing a lower count than the server has.
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const target = state.notifications.find((n) => n.id === action.payload);
        if (target && !target.is_read) {
          target.is_read = true;
          target.read_at = new Date().toISOString();
        }
      });
  },
});

// `count` is the total number of notifications, not the unread total, so the
// badge has to derive unread from the loaded page.
export const selectUnreadCount = (state) =>
  (state.notifications?.notifications || []).filter((n) => !n.is_read).length;

export const { addRealtimeNotification } = notificationsSlice.actions;
export default notificationsSlice.reducer;
